/**
 * Home Page Component
 * O painel inicial: uma lista de blocos vinda do servidor, desenhada na ordem do peso.
 *
 * Esta tela não decide mais quais seções existem. Quem decide é o servidor, que já aplicou a
 * régua de acesso do contexto ativo (#443, §3.1) — aqui só se ordena e se desenha.
 *
 * Sem bloco nenhum, ela não explica a ausência: eram três vazios diferentes, e dois deles
 * diziam a quem estava olhando que ele não podia ver o painel, o que não resolve nada de quem
 * lê. No lugar dos três entra um fallback só — o próximo cadastro que o painel espera, quando
 * dá para saber qual é, e as ações que a pessoa opera naquele contexto.
 *
 * @version 2025.3.0
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Grid, Skeleton, Stack, Alert } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import { useAuth } from '../stores/authStore';
import { useManifesto } from '../stores/manifestoStore';
import { PageShell } from '../components';
import { EmptyState, type EmptyStateConfig } from '../components/common';
import { BlocoDoPainel, OnboardingBanner, QuickActions, ordenarBlocos } from './dashboard/components';
import { ESTILO_DA_LINHA, comporGrade, estiloDoItem } from './dashboard/composicao';
import dashboardService from '../services/dashboardService';
import branding from '../config/branding';
import { CATALOGO_DE_TELAS } from '../config/menuConfig';
import { PREREQ_CHAINS, PREREQ_NODES } from '../config/prerequisites';
import { avaliar, pode } from '../authorization/pode';
import { usePrerequisites, type PrerequisiteState } from '../hooks/usePrerequisites';
import type { BlocoMontado, DashboardData, TipoDeBloco } from '../types/dashboard';
import type { Manifesto } from '../types/manifesto';
import { motivoDaRecusa } from '../utils/acessoNegado';

/** A cadeia de cadastros de que o painel da empresa depende para medir alguma coisa (§6). */
const CADEIA_DO_PAINEL = 'painel';

/**
 * A tela que resolve o elo e a permissão de criar nela — que sai da de ler, trocando o sufixo
 * (`department.read` → `department.create`), a mesma conta do estado vazio das listagens.
 */
const criacaoDoElo = (elo: string): { caminho: string; permissao: string } | undefined => {
  const tela = CATALOGO_DE_TELAS.find((item) => item.id === PREREQ_NODES[elo]?.node);
  if (!tela?.permissao) return undefined;
  return { caminho: tela.caminho, permissao: `${tela.permissao.split('.')[0]}.create` };
};

/**
 * A pessoa cria este cadastro no contexto em que está? É a régua dos atalhos — `avaliar`, com
 * as quatro perguntas do contexto ativo —, e não uma segunda escrita dela.
 *
 * Dentro de uma empresa, "cadastrar empresa" é um passo que não existe: o item de menu está
 * lá, porque quem administra a empresa lê a ficha dela, mas a permissão de criar não vem no
 * contexto. Era esse elo que abria a cadeia do painel, e ele chegou à tela como próximo passo.
 */
const podeCriar = (elo: string, manifesto: Manifesto): boolean => {
  const criacao = criacaoDoElo(elo);
  return criacao ? avaliar(criacao.caminho, manifesto, pode(criacao.permissao)).liberado : false;
};

/**
 * O cadastro que o painel ainda espera: o primeiro elo pendente que esta pessoa **pode fazer**.
 *
 * Dois cortes, e os dois derrubam o elo sem deixar rastro na tela. Elo que não voltou na
 * resposta é elo que o servidor não deixa esta pessoa ler; elo que ela não cria só ofereceria
 * uma saída que não é dela. Sobrando nenhum, o painel fica com as ações rápidas.
 */
const eloPendente = (
  estado: PrerequisiteState,
  cadeia: string[],
  manifesto: Manifesto | null
): string | undefined =>
  estado.status === 'ready' && manifesto
    ? cadeia.find(
        (elo) =>
          elo in estado.nodes &&
          !estado.nodes[elo] &&
          elo in PREREQ_NODES &&
          podeCriar(elo, manifesto)
      )
    : undefined;

/**
 * O fallback do painel sem nada a mostrar: a frase do próximo cadastro e o botão dele, que o
 * `EmptyState` só oferece a quem pode criar aquilo.
 *
 * O rótulo curto do elo é o único do catálogo que não muda com o vínculo de quem lê — e é o que
 * serve aqui, porque nesta tela nada se diz sobre o acesso de ninguém.
 */
const porOndeComecar = (elo: string): EmptyStateConfig => {
  const passo = PREREQ_NODES[elo];
  return {
    title: 'Por onde começar',
    description: passo.short
      ? `${passo.short} — o painel começa a medir a partir daí.`
      : 'O painel começa a medir a partir do próximo passo.',
    action: { label: passo.actionLabel, node: passo.node, deniedHint: passo.deniedHint },
  };
};

/**
 * O número que cada formato usa para decidir se tem conteúdo a desenhar — o mesmo que o card
 * consulta antes de trocar o corpo por "nenhum". Zero em todos eles é a caixa vazia que a
 * pessoa lê na tela.
 *
 * Os de duas medidas só estão vazios com as duas em zero: o funil e o medidor mostram a semana
 * abaixo do dia, e um dia parado numa semana movimentada continua tendo o que relatar.
 */
const MEDIDA_DO_VAZIO: Partial<Record<TipoDeBloco, string[][]>> = {
  cartao: [['total']],
  funil: [['today', 'total'], ['thisWeek', 'total']],
  medidor: [['today', 'totalSlots'], ['thisWeek', 'totalSlots']],
  canal: [['totalCount']],
  fila: [['total']],
};

const campoDe = (fonte: unknown, campo: string): unknown =>
  typeof fonte === 'object' && fonte !== null
    ? (fonte as Record<string, unknown>)[campo]
    : undefined;

const numeroEm = (conteudo: unknown, caminho: readonly string[]): number | null => {
  const valor = caminho.reduce(campoDe, conteudo);
  return typeof valor === 'number' ? valor : null;
};

/**
 * O bloco não tem o que relatar: lista sem alerta, tabela sem linha, cartão em zero, funil sem
 * atendimento, medidor sem vaga, canal sem instância, fila sem mensagem.
 *
 * Formato que não se sabe medir, e conteúdo fora do formato esperado, contam como movimento. Na
 * dúvida o painel se desenha — errar para o outro lado esconderia número existente.
 */
const blocoSemDado = (bloco: BlocoMontado): boolean => {
  if (Array.isArray(bloco.conteudo)) return bloco.conteudo.length === 0;
  const medidas = MEDIDA_DO_VAZIO[bloco.tipo];
  return medidas ? medidas.every((caminho) => numeroEm(bloco.conteudo, caminho) === 0) : false;
};

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const { manifesto } = useManifesto();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardService.getDashboard();
      setDashboardData(data);
    } catch (err) {
      // Recusa não é falha: é painel sem bloco nenhum, e isso já tem desenho. Tratá-la como
      // erro punha um alerta vermelho e um corpo vazio no lugar do próximo passo.
      if (motivoDaRecusa(err)) {
        setDashboardData({ blocos: [] });
      } else {
        console.error('Error fetching dashboard data:', err);
        setError('Erro ao carregar dados do dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  })();

  const currentDate = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const StatCardSkeleton = () => (
    <Box sx={{ p: 2 }}>
      <Skeleton variant="text" width="30%" height={60} />
      <Skeleton variant="text" width="60%" height={24} />
    </Box>
  );

  const blocos = ordenarBlocos(dashboardData?.blocos ?? []);
  const painelCarregado = !loading && !error && dashboardData !== null;

  // A cadeia só é consultada quando o painel não tem nada a relatar — lista com dado nunca
  // pergunta pelos pré-requisitos (#385). Hoje só a empresa tem cadeia; nas outras perspectivas
  // o fallback fica só com as ações rápidas, e nenhuma consulta sai.
  const painelVazio = painelCarregado && blocos.every(blocoSemDado);
  const daEmpresa = manifesto?.contextoAtivo.tipo === 'empresa';
  const cadeia = daEmpresa ? PREREQ_CHAINS[CADEIA_DO_PAINEL] ?? [] : [];
  const prerequisitos = usePrerequisites(cadeia, painelVazio && cadeia.length > 0);
  const proximoPasso = eloPendente(prerequisitos, cadeia, manifesto);

  // Largura e altura de cada linha saem da composição da grade: aqui só se desenha o que ela
  // decidiu. Todos os blocos de uma linha ficam com a altura do mais alto, para que uma
  // listagem curta ocupe a caixa inteira em vez de deixar o degrau ao lado do vizinho.
  const gradeDeBlocos = (
    <Stack spacing={3} sx={{ mb: 4 }}>
      {comporGrade(blocos).map((linha, indice) => (
        <Box key={`${linha.grupo}-${indice}`} sx={ESTILO_DA_LINHA}>
          {linha.blocos.map((bloco) => (
            <Box key={bloco.id} sx={estiloDoItem(linha.grupo)}>
              <BlocoDoPainel bloco={bloco} loading={loading} />
            </Box>
          ))}
        </Box>
      ))}
    </Stack>
  );

  const corpoDoPainel = (() => {
    if (loading) {
      return (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCardSkeleton />
            </Grid>
          ))}
        </Grid>
      );
    }
    // Zero por falta de cadastro não é conformidade: aí o painel diz o próximo passo. Zero com
    // a base cadastrada é a grade que responde, e é ela que diz que não há pendência.
    if (painelVazio && (blocos.length === 0 || proximoPasso)) {
      return proximoPasso ? (
        <Box sx={{ mb: 4 }}>
          <EmptyState config={porOndeComecar(proximoPasso)} />
        </Box>
      ) : null;
    }
    return gradeDeBlocos;
  })();

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início' }]}
      title={`${greeting}, ${user?.name?.split(' ')[0] || 'Usuário'}!`}
      subtitle={branding.appDescription}
      actions={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <CalendarMonth fontSize="small" />
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {currentDate}
          </Typography>
        </Box>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* O aviso convive com o "Por onde começar" do painel vazio, mas não aparece junto dele. */}
      <OnboardingBanner suprimido={painelVazio && Boolean(proximoPasso)} />

      {corpoDoPainel}

      <QuickActions />
    </PageShell>
  );
};

export default HomePage;
